import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { type NotificationSettingsSchema } from '@strata/react-dialog';
import { InformationIcon } from '@strata/react-status-icons';
import { Worklist } from '@strata/react-worklist';
import { useFormikContext } from 'formik';

import { NotificationTypes } from 'components/notifications/NotificationComponentMap';
import { type WorklistSummaryPreferences } from 'store/api/notificationApi';

const WorklistSummaryCategoryOptions = [
    {
        value: Worklist.WorkTaskCategory.CLAIMS,
        text: 'Claims Tasks',
        infoText: 'Tasks relating to claims such as "Rebilling" tasks.',
    },
    {
        value: Worklist.WorkTaskCategory.PENDREFER,
        text: 'Pending Referral Tasks',
        infoText: 'Tasks relating to pending referrals such as "Pend. Intake" tasks.',
    },
    {
        value: Worklist.WorkTaskCategory.PERF,
        text: 'Performance Tasks',
        infoText:
            'Tasks relating to performance requirements such as "Document Disease State Assessment" tasks.',
    },
    {
        value: Worklist.WorkTaskCategory.REFERRAL,
        text: 'Referral Tasks',
        infoText: 'Tasks relating to Referrals such as "Confirm/Update Needs By Date" tasks.',
    },
    {
        value: Worklist.WorkTaskCategory.SPCONFIG,
        text: 'Configuration Tasks',
        infoText:
            'Tasks relating to specialty pharmacy configuration such as "Confirm LDD Access" tasks.',
    },
];

export type WorklistSummaryNotificationSchema = {
    worklistSummaryCategories?: string[];
} & NotificationSettingsSchema;

export const WorklistSummarySettings = () => {
    const formik = useFormikContext<WorklistSummaryNotificationSchema>();

    return (
        <div className="padding__bottom--medium">
            <CheckboxListFormik
                name="worklistSummaryCategories"
                options={WorklistSummaryCategoryOptions.map((item) => ({
                    value: item.value,
                    component: (
                        <span style={{ display: 'inline-flex', gap: '8px' }}>
                            <span>{item.text}</span>
                            <InformationIcon tooltipText={item.infoText} size={16} />
                        </span>
                    ),
                }))}
                disabled={formik.values.email?.includes(NotificationTypes.WORKSUM) === false}
                onChange={(values) => {
                    void (async () => {
                        await formik.setFieldValue('worklistSummaryCategories', values);
                        if (values.length === 0) {
                            await formik.setFieldValue(
                                'email',
                                formik.values.email?.filter(
                                    (item) => item !== NotificationTypes.WORKSUM,
                                ),
                            );
                        }
                    })();
                }}
                inline
            />
        </div>
    );
};

WorklistSummarySettings.Options = WorklistSummaryCategoryOptions;

WorklistSummarySettings.initialValues = (preferences?: WorklistSummaryPreferences) => ({
    worklistSummaryCategories:
        preferences?.task_categories?.find((item) => item.code === NotificationTypes.WORKSUM)
            ?.categories ?? [],
});

WorklistSummarySettings.submitValues = (
    values: WorklistSummaryNotificationSchema,
): WorklistSummaryPreferences => ({
    task_categories: [
        {
            code: NotificationTypes.WORKSUM,
            categories: values.worklistSummaryCategories,
        },
    ],
});
